import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import {
  formatCalendarDay,
  formatMonth,
  formatNumber,
  formatStoreDateTime,
  formatStoreShift,
  weekdayNames,
} from '@/lib/format';
import { formatStoreTime, monthBounds, storeToday } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import type { TimesheetDay, TimesheetEntry } from './timesheets.api';

/**
 * The by-shift reading, laid out as a month.
 *
 * A shift sits on the day it started, which is the day the API files it under,
 * so a cell here and a row of the by-day list always agree. A wide screen has
 * room to name the shifts in the cell; a phone gets a count and the hours, and
 * either way picking a day lists its shifts in full underneath.
 */

/** How many shifts a cell names before it says "+N more". */
const VISIBLE = 3;

interface Shift {
  who: string;
  entry: TimesheetEntry;
}

function shiftMonth(month: string, by: number) {
  const [year, index] = month.split('-').map(Number);
  return new Date(Date.UTC(year, index - 1 + by, 1)).toISOString().slice(0, 7);
}

/** Whole weeks, Monday first; `null` pads the days outside the month. */
function cellsFor(month: string): (string | null)[] {
  const [year, index] = month.split('-').map(Number);
  const lead = (new Date(Date.UTC(year, index - 1, 1)).getUTCDay() + 6) % 7;
  const length = Number(monthBounds(month).end.slice(8));
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let day = 1; day <= length; day++) {
    cells.push(`${month}-${String(day).padStart(2, '0')}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const hoursOf = (shifts: Shift[]) =>
  shifts.reduce((sum, shift) => sum + (shift.entry.hours ?? 0), 0);

export function TimesheetCalendar({
  month,
  onMonthChange,
  days,
  busy,
  typeName,
  onOpen,
}: {
  /** `yyyy-MM`. */
  month: string;
  onMonthChange: (month: string) => void;
  days: TimesheetDay[];
  /** True while the month is being fetched. */
  busy?: boolean;
  typeName: (type: TimesheetEntry['type']) => string;
  onOpen: (entry: TimesheetEntry) => void;
}) {
  const { t } = useTranslation();
  const today = storeToday();
  const [picked, setPicked] = useState<string | null>(null);

  const byDay = useMemo(() => {
    const map = new Map<string, Shift[]>();
    for (const day of days) {
      const key = formatStoreTime(day.date, true);
      const shifts = map.get(key) ?? [];
      for (const entry of day.timesheet ?? []) {
        shifts.push({ who: day.user?.completeName ?? '', entry });
      }
      map.set(key, shifts);
    }
    for (const shifts of map.values()) {
      shifts.sort((a, b) => a.entry.startTime.localeCompare(b.entry.startTime));
    }
    return map;
  }, [days]);

  const cells = useMemo(() => cellsFor(month), [month]);

  // A pick belongs to the month it was made in. Without one, today is the day
  // most likely wanted, when today is on the page at all.
  const shown = picked?.startsWith(month)
    ? picked
    : today.startsWith(month)
      ? today
      : null;
  const shownShifts = shown ? (byDay.get(shown) ?? []) : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('timesheets.previousMonth')}
            disabled={busy}
            onClick={() => onMonthChange(shiftMonth(month, -1))}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('timesheets.nextMonth')}
            disabled={busy}
            onClick={() => onMonthChange(shiftMonth(month, 1))}
          >
            <ChevronRight />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy || today.startsWith(month)}
            onClick={() => onMonthChange(today.slice(0, 7))}
          >
            {t('timesheets.thisMonth')}
          </Button>
        </div>
        <p className="text-sm font-semibold first-letter:uppercase">
          {formatMonth(month)}
        </p>
      </div>

      <div className="relative rounded-xl border">
        <BusyOverlay show={busy} />
        {/* The clipping lives a level down: the overlay's spinner is sticky,
            and an overflow box around it would pin it to the grid. */}
        <div className="overflow-hidden rounded-[inherit]">
          <div className="bg-muted/40 text-muted-foreground grid grid-cols-7 border-b text-center text-xs font-medium">
            {weekdayNames().map((name) => (
              <div key={name} className="truncate py-2">
                {name}
              </div>
            ))}
          </div>

          <div className="-mb-px grid grid-cols-7">
            {cells.map((key, index) => {
              if (!key) {
                return (
                  <div
                    key={`pad-${index}`}
                    className="bg-muted/20 min-h-16 border-r border-b lg:min-h-28 [&:nth-child(7n)]:border-r-0"
                  />
                );
              }

              const shifts = byDay.get(key) ?? [];
              const more = shifts.length - VISIBLE;

              return (
                <div
                  key={key}
                  onClick={() => setPicked(key)}
                  className={cn(
                    'min-h-16 min-w-0 cursor-pointer border-r border-b p-1 transition-colors lg:min-h-28 lg:p-1.5 [&:nth-child(7n)]:border-r-0',
                    shown === key ? 'bg-accent' : 'hover:bg-accent/50'
                  )}
                >
                  <div className="flex items-center justify-between gap-1">
                    {/* No handler of its own: the click reaches the cell, and
                        this is what puts the day within reach of a keyboard. */}
                    <button
                      type="button"
                      aria-pressed={shown === key}
                      aria-label={formatCalendarDay(key)}
                      className={cn(
                        'flex size-6 shrink-0 items-center justify-center rounded-full text-xs tabular-nums',
                        key === today &&
                          'bg-primary text-primary-foreground font-semibold'
                      )}
                    >
                      {Number(key.slice(8))}
                    </button>
                    {shifts.length > 0 && (
                      <span className="text-muted-foreground hidden truncate text-xs tabular-nums lg:inline">
                        {formatNumber(hoursOf(shifts))} h
                      </span>
                    )}
                  </div>

                  {/* Too narrow to name anybody: how many, and how long. */}
                  {shifts.length > 0 && (
                    <div className="mt-1 text-center lg:hidden">
                      <span className="bg-secondary text-secondary-foreground rounded-full px-1.5 py-0.5 text-xs tabular-nums">
                        {shifts.length}
                      </span>
                      <p className="text-muted-foreground mt-0.5 truncate text-[10px] tabular-nums">
                        {formatNumber(hoursOf(shifts))} h
                      </p>
                    </div>
                  )}

                  <div className="mt-1 hidden space-y-0.5 lg:block">
                    {shifts.slice(0, VISIBLE).map(({ who, entry }) => (
                      <button
                        key={entry.id}
                        type="button"
                        title={`${who} · ${formatStoreShift(
                          entry.startTime,
                          entry.endTime,
                          t('timesheets.stillOpen'),
                          false
                        )}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpen(entry);
                        }}
                        className="bg-secondary text-secondary-foreground hover:bg-secondary/70 block w-full truncate rounded px-1 py-0.5 text-left text-xs transition-colors"
                      >
                        <span className="tabular-nums">
                          {formatStoreDateTime(entry.startTime, 'time')}
                        </span>{' '}
                        {who}
                      </button>
                    ))}
                    {more > 0 && (
                      <p className="text-muted-foreground px-1 text-xs">
                        {t('timesheets.moreShifts', { count: more })}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {shown ? (
        <div className="rounded-xl border">
          <div className="flex items-baseline justify-between gap-3 border-b p-3">
            <p className="text-sm font-medium first-letter:uppercase">
              {formatCalendarDay(shown)}
            </p>
            {shownShifts.length > 0 && (
              <p className="text-muted-foreground text-xs tabular-nums">
                {t('timesheets.nShifts', { count: shownShifts.length })} ·{' '}
                {formatNumber(hoursOf(shownShifts))} h
              </p>
            )}
          </div>

          {shownShifts.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {t('timesheets.noShiftsThatDay')}
            </p>
          ) : (
            <div className="divide-y">
              {shownShifts.map(({ who, entry }) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => onOpen(entry)}
                  className="hover:bg-accent flex w-full items-center gap-3 p-3 text-left transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{who}</p>
                    <p className="text-muted-foreground truncate text-xs">
                      {formatStoreShift(
                        entry.startTime,
                        entry.endTime,
                        t('timesheets.stillOpen'),
                        false
                      )}
                    </p>
                    {entry.comment && (
                      <p className="text-muted-foreground truncate text-xs">
                        {entry.comment}
                      </p>
                    )}
                  </div>
                  {entry.type && (
                    <Badge variant="secondary" className="shrink-0">
                      {typeName(entry.type)}
                    </Badge>
                  )}
                  <span className="shrink-0 text-sm font-semibold tabular-nums">
                    {entry.hours == null ? '—' : formatNumber(entry.hours)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="text-muted-foreground rounded-xl border border-dashed py-8 text-center text-sm">
          {t('timesheets.pickDay')}
        </p>
      )}
    </div>
  );
}
